import { Controller } from "@hotwired/stimulus";
import { launchConfetti } from "../utils/confetti";

const SEARCH_LABEL = "Email, provider, or office name";
const INVITE_LABEL = "Office or Provider Email";

export default class extends Controller {
  static targets = [
    "emailInput",
    "emailLabel",
    "preferredNameInput",
    "providerListDropzone",
    "providerDzItems",
    "addEmail",
    "addEmailWrapper",
    "existingEmailWrapper",
    "existingProviderName",
    "newReferralPath",
    "submitButton",
    "form",
  ];

  // The one input is both the search box and the submitted invitation email.
  // "Invite mode" (issue #2983) is entered once the office isn't on Sindi:
  // the label switches to the email the invite will go to, the preferred-name
  // field shows, and the input only accepts an email.
  emailInputTargetConnected(e) {
    // Check if email is pre-filled and show preferred name field if needed
    if (this.emailInputTarget.value.length) {
      this.enterInviteMode();
      this.checkSubmitButton();
    }

    e.addEventListener("focusout", (e) => {
      if (this.inviteMode) {
        // Already inviting (e.g. just clicked "Add X" and the email is still
        // empty) — keep the preferred name the user typed.
      } else if (!!!this.providerDzItemsTarget.children.length && this.emailInputTarget.value.length) {
        this.enterInviteMode();
      } else {
        this.exitInviteMode();
      }

      this.checkSubmitButton();

      setTimeout(() => {
        this.providerListDropzoneTarget.classList.add("d-none");
      }, 200);
    });
  };

  preferredNameInputTargetConnected(e) {
    e.addEventListener("input", (e) => {
      this.checkSubmitButton();
    });
  };

  searchProviders(e) {
    // In invite mode the top field is just the recipient's email — no lookup,
    // no dropdown.
    if (this.inviteMode) {
      this.providerListDropzoneTarget.classList.add("d-none");
      this.checkSubmitButton();
      return;
    }

    setTimeout(() => {
      this.providerListDropzoneTarget.classList.remove("d-none");
      this.existingEmailWrapperTarget.classList.add("d-none");
      const searchText = e.target.value;

      this.checkSubmitButton();

      if (this.inviteMode) {
        // Invite mode began during the 300ms debounce — nothing to search.
        this.providerListDropzoneTarget.classList.add("d-none");
        this.checkSubmitButton();
      } else if (!searchText.length) {
        this.providerListDropzoneTarget.classList.add("d-none");
        this.resetAndHideFields();
      } else {
        $.ajax({
          url: '/searchs/providers',
          type: "POST",
          dataType: "HTML",
          data: { search_text: searchText },
          success: (data) => {
            this.providerDzItemsTarget.innerHTML = data;
            if (data) {
              this.providerItemClick();
              this.addEmailWrapperTarget.classList.add("d-none");
            } else if (this.inviteMode) {
              // A search that was in flight when invite mode started — drop it.
              this.providerDzItemsTarget.innerHTML = "";
              this.providerListDropzoneTarget.classList.add("d-none");
            } else {
              this.addEmailWrapperTarget.classList.remove("d-none");
              this.addEmailTarget.innerHTML = searchText;
            }
          },
        });

        this.providerListDropzoneTarget.classList.remove("d-none");
      }
    }, 300);
  }

  // "+ Add <typed text>" for an office that isn't on Sindi. What was typed is
  // either the email the invite goes to or the office/provider name — route it
  // to the right field and ask for the other. (mousedown: fires before the
  // input's focusout hides the dropdown.)
  addNewOffice(e) {
    e.preventDefault();
    const typed = this.emailInputTarget.value.trim();
    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typed);

    this.enterInviteMode();
    this.providerDzItemsTarget.innerHTML = "";
    this.providerListDropzoneTarget.classList.add("d-none");
    this.existingEmailWrapperTarget.classList.add("d-none");

    if (looksLikeEmail) {
      this.preferredNameInputTarget.focus();
    } else {
      if (!this.preferredNameInputTarget.value.length) this.preferredNameInputTarget.value = typed;
      this.emailInputTarget.value = "";
      this.emailInputTarget.focus();
    }

    this.checkSubmitButton();
  }

  enterInviteMode() {
    this.inviteMode = true;
    this.preferredNameInputTarget.closest('.row').classList.remove("d-none");
    this.emailInputTarget.type = "email";
    this.emailInputTarget.placeholder = INVITE_LABEL;
    if (this.hasEmailLabelTarget) this.emailLabelTarget.textContent = INVITE_LABEL;
  }

  exitInviteMode() {
    this.inviteMode = false;
    this.preferredNameInputTarget.closest('.row').classList.add("d-none");
    this.emailInputTarget.type = "text";
    this.emailInputTarget.placeholder = SEARCH_LABEL;
    if (this.hasEmailLabelTarget) this.emailLabelTarget.textContent = SEARCH_LABEL;
  }

  providerItemClick() {
    document.querySelectorAll("li.provider-item").forEach((item) => {
      item.addEventListener("click", () => {
        if (item.dataset.imported === "true") {
          // Imported office (not on Sindi yet) - prefill an invitation with
          // the office name and its contact email
          this.emailInputTarget.value = item.dataset.organizationEmail || "";
          this.preferredNameInputTarget.value = item.dataset.organizationName || "";
          this.enterInviteMode();
          this.existingEmailWrapperTarget.classList.add("d-none");
          this.providerDzItemsTarget.innerHTML = "";
          this.providerListDropzoneTarget.classList.add("d-none");
          this.checkSubmitButton();
        } else {
          this.emailInputTarget.value = item.dataset.providerEmail;
          this.existingProviderNameTarget.innerHTML = item.dataset.providerName;
          this.newReferralPathTarget.attributes.href.value = item.dataset.newReferralPath;
          this.existingEmailWrapperTarget.classList.remove("d-none");

          // Existing user: no invite, so back to the search label
          this.exitInviteMode();
        }
      });
    });
  }

  resetAndHideFields() {
    this.exitInviteMode();
    this.checkSubmitButton();
  }

  checkSubmitButton() {
    const hasProviders = this.providerDzItemsTarget.children.length > 0;
    const hasPreferredName = this.preferredNameInputTarget.value.trim().length > 0;
    const hasEmail = this.emailInputTarget.value.trim().length > 0;

    if (!hasProviders && hasPreferredName && hasEmail) {
      this.formTarget.dataset.preventSubmit = "false";
      this.submitButtonTarget.classList.remove("btn-lock");
      this.submitButtonTarget.disabled = false;
    } else {
      this.formTarget.dataset.preventSubmit = "true";
      this.submitButtonTarget.classList.add("btn-lock");
      this.submitButtonTarget.disabled = true;
    }
  }

  connect() {
    this.formTarget.addEventListener("submit", (e) => {
      if (this.formTarget.dataset.preventSubmit === "true") {
        e.preventDefault();
      }
    });

    // Track the pointer position of the most recent click so the confetti can
    // burst from exactly where the user clicked "Add" (capture phase)
    this.element.addEventListener("pointerdown", (event) => {
      this.lastPointerDown = { x: event.clientX, y: event.clientY, at: Date.now() };
    }, true);

    // Celebrate a sent invitation with the big confetti at the click origin
    this.formTarget.addEventListener("turbo:submit-end", (event) => {
      if (!event.detail?.success) return;

      let origin;
      if (this.lastPointerDown && Date.now() - this.lastPointerDown.at < 1000) {
        origin = { x: this.lastPointerDown.x, y: this.lastPointerDown.y };
      } else {
        const rect = this.submitButtonTarget.getBoundingClientRect();
        origin = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      }

      launchConfetti(origin, { path: "/animations/big_confetti.json", size: 500 });
    });
  }
}